import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { formatFy, getSectorChipClasses, normalizeGeographyLabel, normalizeImplementationModeLabel, normalizeSectorLabel } from "../lib/display";

const formatAmount = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function ProjectRecordsTable({ records, loading, page, pageSize, total, onPageChange, emptyMessage = "No matching records found.", hideSectorColumn = false }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-5">
      <div className="overflow-x-auto border border-slate-200" data-testid="all-records-table-wrapper">
        <table className="min-w-full border-collapse text-left text-sm" data-testid="all-records-table">
          <thead className="bg-slate-950 text-white">
            <tr>
              <th className="px-4 py-3 font-semibold">Company Name</th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap">FY</th>
              <th className="px-4 py-3 font-semibold">State</th>
              <th className="px-4 py-3 font-semibold">District (Source)</th>
              <th className="px-4 py-3 font-semibold">CSR Project Name</th>
              {!hideSectorColumn ? <th className="min-w-[220px] px-4 py-3 font-semibold">Sector</th> : null}
              <th className="px-4 py-3 font-semibold">Implementation Mode</th>
              <th className="px-4 py-3 font-semibold">Outlay (Cr.)</th>
              <th className="px-4 py-3 font-semibold">Spent (Cr.)</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record, index) => (
              <tr key={`${record.company_id}-${record.fiscal_year}-${record.district_lgd_code}-${index}`} className="border-b border-slate-200 even:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">
                  <Link to={`/company/${record.company_id}`} className="text-[#1E3A8A] underline decoration-transparent underline-offset-4 transition-colors duration-200 hover:text-[#F26522] hover:decoration-[#F26522]" data-testid={`records-company-name-link-${record.company_id}-${index}`}>
                    {record.company_name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatFy(record.fiscal_year)}</td>
                <td className="px-4 py-3 text-slate-700">{normalizeGeographyLabel(record.state)}</td>
                <td className="px-4 py-3 text-slate-700">{normalizeGeographyLabel(record.district_as_per_source)}</td>
                <td className="px-4 py-3 text-slate-700">{record.csr_project_name}</td>
                {!hideSectorColumn ? (
                  <td className="min-w-[220px] px-4 py-3 text-slate-700">
                    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getSectorChipClasses(record.sector)}`}>
                      {normalizeSectorLabel(record.sector)}
                    </span>
                  </td>
                ) : null}
                <td className="px-4 py-3 text-slate-700">{normalizeImplementationModeLabel(record.implementation_mode)}</td>
                <td className="px-4 py-3 font-semibold text-slate-900">{formatAmount(record.project_amount_outlay)}</td>
                <td className="px-4 py-3 font-semibold text-slate-900">{formatAmount(record.amount_spent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && records.length === 0 ? (
        <div className="border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600" data-testid="all-records-empty-state">
          {emptyMessage}
        </div>
      ) : null}

      <div className="flex flex-col gap-4 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-slate-500" data-testid="all-records-count-summary">
          {loading ? "Loading matching rows..." : `${total.toLocaleString("en-IN")} matching records`}
        </div>

        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" className="rounded-none" disabled={page === 1} onClick={() => onPageChange(Math.max(1, page - 1))} data-testid="all-records-previous-button">
            Previous
          </Button>
          <span className="text-sm font-semibold text-slate-700" data-testid="all-records-pagination-indicator">
            Page {page} of {totalPages}
          </span>
          <Button type="button" className="rounded-none bg-[#F26522] text-white hover:bg-[#D9561B]" disabled={page >= totalPages} onClick={() => onPageChange(Math.min(totalPages, page + 1))} data-testid="all-records-next-button">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}